'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: string;
  href?: string;
  action?: () => void;
  category: string;
  keywords: string[];
}

const allCommands: CommandItem[] = [
  { id: 'dashboard', label: 'Dashboard', description: 'Your business overview', icon: '⬡', href: '/dashboard', category: 'Navigate', keywords: ['home', 'overview', 'summary'] },
  { id: 'onboarding', label: 'Start Company Registration', description: 'Register a new company with AI', icon: '🏢', href: '/onboarding', category: 'Navigate', keywords: ['company', 'register', 'incorporate', 'pvt ltd', 'llp'] },
  { id: 'trademark', label: 'Trademark Search', description: 'Search and assess trademark risk', icon: '™', href: '/trademark', category: 'Navigate', keywords: ['brand', 'trademark', 'search', 'risk'] },
  { id: 'trademark-status', label: 'Trademark Status', description: 'Monitor application status', icon: '⊙', href: '/trademark/status', category: 'Navigate', keywords: ['trademark', 'status', 'monitor', 'objection'] },
  { id: 'gst', label: 'GST Registration', description: 'Prepare GST registration', icon: '₹', href: '/gst', category: 'Navigate', keywords: ['gst', 'tax', 'registration'] },
  { id: 'msme', label: 'MSME / Udyam', description: 'Check eligibility and register', icon: '◫', href: '/msme', category: 'Navigate', keywords: ['msme', 'udyam', 'small', 'medium', 'enterprise'] },
  { id: 'dpiit', label: 'DPIIT Startup Recognition', description: 'Startup India recognition', icon: '🇮🇳', href: '/dpiit', category: 'Navigate', keywords: ['dpiit', 'startup', 'india', 'recognition'] },
  { id: 'examination', label: 'Examination Report', description: 'Upload and analyse objections', icon: '⊕', href: '/examination', category: 'Navigate', keywords: ['examination', 'objection', 'report', 'ocr'] },
  { id: 'documents', label: 'Documents', description: 'Manage your business documents', icon: '◻', href: '/documents', category: 'Navigate', keywords: ['documents', 'files', 'upload', 'aadhaar', 'pan'] },
  { id: 'compliance', label: 'Compliance', description: 'Deadlines and filings', icon: '✓', href: '/compliance', category: 'Navigate', keywords: ['compliance', 'deadline', 'filing', 'tds', 'roc'] },
  { id: 'assistant', label: 'AI Assistant', description: 'Ask anything about your business', icon: '◎', href: '/assistant', category: 'Navigate', keywords: ['ai', 'assistant', 'ask', 'question', 'help'] },
  { id: 'review', label: 'Pre-Filing Review', description: 'Human approval gate before filing', icon: '🔍', href: '/review', category: 'Navigate', keywords: ['review', 'approve', 'file', 'submit', 'gate'] },
  { id: 'advisor', label: 'Advisor Portal', description: 'CA and advisor dashboard', icon: '⊞', href: '/advisor', category: 'Navigate', keywords: ['advisor', 'ca', 'chartered', 'accountant', 'portfolio'] },
  { id: 'business', label: 'Business Passport', description: 'Your business identity profile', icon: '◈', href: '/business', category: 'Navigate', keywords: ['business', 'passport', 'profile', 'identity'] },
];

interface CommandCenterProps {
  open: boolean;
  onClose: () => void;
}

export default function CommandCenter({ open, onClose }: CommandCenterProps) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  const filtered = query.trim()
    ? allCommands.filter(cmd =>
        cmd.label.toLowerCase().includes(query.toLowerCase()) ||
        cmd.description?.toLowerCase().includes(query.toLowerCase()) ||
        cmd.keywords.some(k => k.includes(query.toLowerCase()))
      )
    : allCommands;

  const grouped = filtered.reduce<Record<string, CommandItem[]>>((acc, cmd) => {
    if (!acc[cmd.category]) acc[cmd.category] = [];
    acc[cmd.category].push(cmd);
    return acc;
  }, {});

  const flatFiltered = Object.values(grouped).flat();

  const execute = useCallback((item: CommandItem) => {
    if (item.href) router.push(item.href);
    if (item.action) item.action();
    onClose();
    setQuery('');
  }, [router, onClose]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setSelected(0);
    }
  }, [open]);

  useEffect(() => {
    setSelected(0);
  }, [query]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(s + 1, flatFiltered.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)); }
      if (e.key === 'Enter' && flatFiltered[selected]) { execute(flatFiltered[selected]); }
      if (e.key === 'Escape') { onClose(); setQuery(''); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, flatFiltered, selected, execute, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh] px-4" onClick={onClose}>
      <div
        className="w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-border overflow-hidden animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border">
          <svg className="w-4 h-4 text-ink-subtle flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search pages, actions, features…"
            className="flex-1 text-[14px] text-ink placeholder-ink-subtle outline-none bg-transparent"
          />
          <kbd className="hidden sm:flex items-center gap-1 text-[10px] font-medium text-ink-subtle bg-surface-3 border border-border px-1.5 py-0.5 rounded-md">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[380px] overflow-y-auto py-2">
          {flatFiltered.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-[13px] text-ink-muted">No results for &quot;{query}&quot;</p>
            </div>
          ) : (
            Object.entries(grouped).map(([category, items]) => (
              <div key={category}>
                <p className="px-4 py-1.5 text-[10px] font-semibold text-ink-subtle uppercase tracking-wider">{category}</p>
                {items.map(item => {
                  const globalIdx = flatFiltered.indexOf(item);
                  return (
                    <button
                      key={item.id}
                      onClick={() => execute(item)}
                      onMouseEnter={() => setSelected(globalIdx)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        selected === globalIdx ? 'bg-ai-light' : 'hover:bg-surface-2'
                      }`}
                    >
                      <span className="text-base w-6 text-center flex-shrink-0">{item.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-[13px] font-medium truncate ${selected === globalIdx ? 'text-ai' : 'text-ink'}`}>
                          {item.label}
                        </p>
                        {item.description && (
                          <p className="text-[11px] text-ink-muted truncate">{item.description}</p>
                        )}
                      </div>
                      {selected === globalIdx && (
                        <kbd className="text-[10px] text-ai bg-ai/10 border border-ai/20 px-1.5 py-0.5 rounded-md font-medium flex-shrink-0">
                          ↵
                        </kbd>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-border flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-[10px] text-ink-subtle">
            <kbd className="bg-surface-3 border border-border px-1 py-0.5 rounded text-[9px]">↑↓</kbd>
            <span>navigate</span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-ink-subtle">
            <kbd className="bg-surface-3 border border-border px-1 py-0.5 rounded text-[9px]">↵</kbd>
            <span>open</span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-ink-subtle">
            <kbd className="bg-surface-3 border border-border px-1 py-0.5 rounded text-[9px]">ESC</kbd>
            <span>close</span>
          </div>
        </div>
      </div>
    </div>
  );
}
